import { Container, Graphics } from "pixi.js";

interface NowLineProps {
  width: number;
  /** Ordonnée de la tête de lecture — l'instant sonore. */
  playheadY: number;
  /**
   * Le délai de restitution, en pixels : le présent est d'autant plus haut que
   * la tête. Zéro éteint le rendu — sans délai, présent et instant sonore se
   * confondent, et la ligne n'aurait rien à séparer.
   */
  playoutDelayPx: number;
}

const NOW_COLOR = 0xf5b301;
const NOW_LINE_WIDTH = 1;

/** Espacement des hachures, en pixels. Assez lâche pour rester discret. */
const HATCH_SPACING = 9;

/**
 * La ligne du présent, et le délai de restitution qui la sépare de la tête.
 *
 * # Ce qu'elle marque
 *
 * Deux instants, et un espace entre les deux :
 *
 * - la **tête de lecture** marque l'instant sonore : une note descend et sonne
 *   en la touchant ;
 * - cette ligne marque le **présent** ;
 * - la bande hachurée entre les deux est le **délai de restitution** — de
 *   combien la tête court derrière le présent, pour laisser aux notes des
 *   partenaires le temps d'arriver avant leur tick et sonner sur la grille,
 *   pas au rythme du réseau.
 *
 * Ce délai est invisible et déroutant — on appuie, rien ne vient, puis tout
 * arrive. La ligne le rend visible : plus la séance attend, plus la bande est
 * épaisse, et le réglage cesse d'être un chiffre dans un panneau.
 *
 * # Ce qu'elle ne sait pas
 *
 * Pourquoi il y a un délai. Le studio ne connaît que le délai de restitution
 * du transport local ; qu'il vienne d'une séance, d'un mode, d'un réglage, ne
 * le regarde pas — c'est pour ça qu'elle ne porte plus le nom du mode de
 * séance qui le pose.
 *
 * Elle ne sait pas non plus d'où naît une touche qu'on enfonce. Celui qui émet
 * capte au présent vrai, donc sur cette ligne ; celui qui écoute capte à sa
 * tête de lecture, donc dessous — son tick ne part chez personne, l'avancer lui
 * ferait entendre son propre jeu avec son propre retard. Le roll ignore dans
 * lequel des deux cas il se trouve, et n'a rien à en dire. Elle a d'abord marqué autre chose aussi : l'endroit où
 * la capture *avançait* son tick pour viser la tête. La géométrie n'a pas
 * bougé, le sens si : la ligne n'est plus l'endroit où l'on triche, c'est
 * l'heure qu'il est.
 *
 * # Pourquoi au-dessus
 *
 * Le roll descend, donc le futur de la tête est en haut. Le présent est `D`
 * devant l'instant sonore, donc `D` plus haut : la ligne est l'horizon de ce
 * qui a déjà été joué mais pas encore entendu.
 *
 * # Pourquoi des hachures et non un aplat
 *
 * Un aplat entrerait en concurrence avec le voile du passé, qui occupe déjà
 * le bas de l'écran, et il masquerait les notes qu'il recouvre. Les hachures se
 * lisent comme une texture, pas comme une couche : on voit à travers.
 */
export class NowLine {
  private readonly graphics = new Graphics();
  private previous?: NowLineProps;

  constructor(stage: Container) {
    stage.addChild(this.graphics);
  }

  destroy(): void {
    this.graphics.destroy();
  }

  draw(props: NowLineProps): void {
    if (!this.shouldRedraw(props)) {
      return;
    }

    this.graphics.clear();

    if (props.playoutDelayPx <= 0) {
      return;
    }

    const top = props.playheadY - props.playoutDelayPx;

    // Un présent au-dessus du haut du rouleau n'a rien à montrer : la bande
    // couvrirait tout l'écran sans rien séparer, et la ligne serait hors champ.
    // C'est le cas du spectateur d'un récital, dont la tête de lecture court
    // plus loin derrière le présent que le rouleau ne montre d'avance, pour
    // que les notes de l'interprète tombent du haut.
    if (top <= 0) {
      return;
    }

    // Les hachures d'abord : la ligne se pose dessus, jamais l'inverse.
    for (let x = -props.playoutDelayPx; x < props.width; x += HATCH_SPACING) {
      this.graphics
        .moveTo(x, props.playheadY)
        .lineTo(x + props.playoutDelayPx, top)
        .stroke({ color: NOW_COLOR, alpha: 0.09, width: 1 });
    }

    this.graphics
      .moveTo(0, top)
      .lineTo(props.width, top)
      .stroke({ color: NOW_COLOR, alpha: 0.75, width: NOW_LINE_WIDTH });
  }

  private shouldRedraw(props: NowLineProps): boolean {
    const previous = this.previous;
    this.previous = { ...props };

    if (previous === undefined) {
      return true;
    }

    return (
      previous.width !== props.width
      || previous.playheadY !== props.playheadY
      || previous.playoutDelayPx !== props.playoutDelayPx
    );
  }
}
